<template>
  <div>
    <div class="comment-form">
      <b-form @submit="onSubmit" @submit.stop.prevent>
        <ValidationProvider rules="required|min:2|max:500" ref="commentField">
          <div class="comment-field-wrapper mb-1">
            <b-form-textarea
              class="text-sm comment-field"
              v-model="form.comment"
              :placeholder="$t('comments.addComment')"
              rows="1"
              max-rows="5"
              @keydown.enter.prevent="onSubmit"
              ref="commentTextarea"
            ></b-form-textarea>
            <div class="button-container">
              <b-button type="submit"
                        :disabled="!form.comment"
                        class="button-submit">
                <font-awesome-icon v-if="!formSending" icon="chevron-circle-right"/>
                <b-spinner v-else variant="secondary" small class="mb-1 mr-1"></b-spinner>
              </b-button>
            </div>
          </div>
        </ValidationProvider>
        <span class="error text-sm d-block">{{ error }}</span>
        <button v-if="!uploadFilesEnabled"
                @click="uploadFilesEnabled = true"
                type="button"
                class="btn btn-secondary btn-rounded text-sm px-4 mt-3">
          {{$t('common.addImages')}}
        </button>
        <div v-if="uploadFilesEnabled" class="mt-3">
          <FileUpload @filesToUpload="updateFilesToUpload"
                      :maxFilesToUpload="maxAttachmentsToUpload"
                      :acceptedMimeTypes="uploadMimeTypes"
                      :sending="formSending"/>
        </div>
      </b-form>
      <div v-if="isCommentHoldStatus" class="message text-secondary text-sm mt-3">
        <p class="bold mb-0">{{$t('comments.thankYouForComment')}}</p>
        <p v-html="$t('comments.commentSentToModeration')" class="m-0"></p>
      </div>
    </div>
    <!-- <div v-else class="not-loggedin text-sm">
      <router-link :to="{name: 'Login'}">{{$t('comments.logIn')}}</router-link> {{$t('comments.or')}}
      <router-link to="/signup">{{$t('comments.register')}}</router-link> {{$t('comments.toComment')}}.
    </div> -->
  </div>
</template>
<script>
import { ValidationProvider, extend } from 'vee-validate';
import { required, max, min } from 'vee-validate/dist/rules';
import { i18n } from 'vue-lang-router';
import axios from 'axios';
import mime from 'mime-types';
import FileUpload from './FileUpload.vue';
import authHeader from '../services/auth.header';
import UploadService from '../services/upload.service';
import { MAX_COMMENT_ATTACHMENTS_TO_UPLOAD, IMAGES_UPLOAD_MIME_TYPES } from '../config';

extend('required', {
  ...required,
  message: i18n.t('forms.thisFieldIsRequired'),
});

extend('min', {
  ...min,
  message: i18n.t('comments.commentMinCharacters'),
});
extend('max', {
  ...max,
  message: i18n.t('comments.commentMaxCharacters'),
});

const uploadService = new UploadService();

export default {
  components: {
    ValidationProvider,
    FileUpload,
  },
  props: {
    materialId: Number || String,
  },
  data() {
    return {
      form: {
        materialId: this.materialId,
        comment: '',
      },
      formSending: false,
      error: '',
      isCommentHoldStatus: false,
      maxAttachmentsToUpload: MAX_COMMENT_ATTACHMENTS_TO_UPLOAD,
      uploadMimeTypes: IMAGES_UPLOAD_MIME_TYPES,
      uploadFilesEnabled: false,
      filesToUpload: [],
    };
  },
  methods: {
    validateField(field) {
      const provider = this.$refs[field];

      // Validate the field
      return provider.validate();
    },
    updateFilesToUpload(files) {
      this.filesToUpload = files;
    },
    assignUploadedFilesToCreatedComment(comment, images) {
      axios.post(
        `${process.env.VUE_APP_API_SUPPORT_URL}/comments/${comment.id}`,
        { acf: { images: { ...images } } },
        { headers: authHeader() },
      )
        .then(() => {
          this.formSending = false;
          this.uploadFilesEnabled = false;
          this.filesToUpload = [];
          // TODO: Support for displaying comment attachments when comment status is approved by default
          this.isCommentHoldStatus = comment.status === 'hold';
          this.form.comment = '';
        })
        .catch(() => {
          this.error = this.$t('common.connectionError');
          this.formSending = false;
        });
    },
    onSubmit() {
      this.error = '';
      this.isCommentHoldStatus = false;
      this.validateField('commentField').then(async (result) => {
        if (!result.valid) {
          this.error = result.errors[0];
          this.$refs.commentTextarea.focus();
        } else {
          this.formSending = true;
          const validFilesToUpload = this.filesToUpload.filter((file) => !file.errors.length);
          if (validFilesToUpload.length) {
            const filesToUpload = validFilesToUpload.map((fileToUpload) => {
              return {
                ...fileToUpload,
                fileName: `comment_${Date.now()}.${mime.extension(fileToUpload.file.type)}`,
              };
            });
            //
            // Upload files
            //
            const uploadedFiles = await uploadService.uploadMediaAndGetResponse(filesToUpload);
            // Check for errors in uploaded files
            if (uploadedFiles.find((file) => file.error)) {
              this.formSending = false;
              this.error = this.$t('common.connectionError');
            } else {
              //
              // Send comment
              //
              this.$store.dispatch('comment/SEND_COMMENT', this.form).then((commentResponse) => {
                //
                // Assign uploaded files to newly created comment
                //
                const imagesData = uploadedFiles.map((image) => {
                  return { image: image.id };
                });
                this.assignUploadedFilesToCreatedComment(commentResponse, imagesData);
              }).catch((error) => {
                this.formSending = false;
                this.error = error;
              });
            }
          } else {
            //
            // Send comment without attachments
            //
            this.$store.dispatch('comment/SEND_COMMENT', this.form).then((response) => {
              this.formSending = false;
              this.isCommentHoldStatus = response.status === 'hold';
              this.form.comment = '';
            }).catch((error) => {
              this.formSending = false;
              this.error = error;
            });
          }
        }
      });
    },
  },
};
</script>
<style lang="scss">
@import '@/assets/scss/common.scss';
.comment-form, .not-loggedin {
  padding: 1.5rem 2rem 2rem 3rem;
  border-radius: 8px;
}
.not-loggedin {
  background: $primary;
  color: white;
  a {
    color: $secondary;
  }
}
.comment-form {
  background-color: #e5e5e5;
  .comment-field-wrapper {
    position: relative;
  }
  .comment-field {
    border: 0;
    border-radius: 8px;
    overflow: auto!important;
    color: #787878;
    padding-right: 50px;
    padding-left: 20px;
    padding-top: 10px;
    padding-bottom: 10px;
  }
  .button-container {
    position: absolute;
    bottom: 0;
    right: 0;
  }
  .button-submit {
    background-color: transparent;
    border: 0;
    padding: 3px 18px 3px 3px;
    max-width: 55px;
    font-size: 20px;
    &:hover, &:focus, &:active, &[disabled] {
      background-color: transparent!important;
      box-shadow: none!important;
    }
    &:focus {
      outline: 0;
      box-shadow: none;
    }
    svg {
      color: $secondary;
    }
  }
  .error {
    color: $tertiary;
  }
}
</style>
